import { AttachmentKind, fetchAttachmentBlob } from '@asd-stan/ballot/api/attachment-download';
import { BallotResultsExport, ExportAttachment } from '@asd-stan/ballot/domain/ballot.entity';
import { downloadBlob } from '@asd-stan/helpers/download-file-by-url';

export interface DownloadProgress {
	/** Bytes of attachment data already fetched. */
	loadedBytes: number;
	/** Total bytes of attachment data to fetch, known up front from the API. */
	totalBytes: number;
	/** 0-100, kept in sync with loadedBytes so the UI never has to compute it. */
	percent: number;
}

interface ZipEntry {
	name: string;
	kind: AttachmentKind;
	attachmentId: number;
	size: number;
}

export class DownloadAbortedError extends Error {
	constructor() {
		super('Download aborted');
		this.name = 'DownloadAbortedError';
	}
}

// One retry absorbs a transient hiccup without turning a genuinely broken
// download into a long stall.
const fetchAttempts = 2;

const maxFileNameLength = 120;

// Illegal on Windows or would introduce an unintended folder level in the archive.
// Spaces and dashes are deliberately kept.
const illegalFileNameCharacters = /[\\/:*?"<>|]/g;

// eslint-disable-next-line no-control-regex
const controlCharacters = /[\x00-\x1f]/g;

const sanitizeFileName = (name: string, fallback: string) => {
	const cleaned = name
		.replace(controlCharacters, '')
		.replace(illegalFileNameCharacters, '-')
		.replace(/\s+/g, ' ')
		.replace(/^[.\s]+|[.\s]+$/g, '')
		.slice(0, maxFileNameLength);

	return cleaned || fallback;
};

/** Keeps a zip folder free of duplicates by suffixing repeats: `a.pdf`, `a (2).pdf`. */
const makeUniqueName = (name: string, taken: Set<string>) => {
	if (!taken.has(name)) {
		taken.add(name);
		return name;
	}

	const dot = name.lastIndexOf('.');
	const base = dot > 0 ? name.slice(0, dot) : name;
	const extension = dot > 0 ? name.slice(dot) : '';

	let counter = 2;
	let candidate = `${base} (${counter})${extension}`;
	while (taken.has(candidate)) {
		counter += 1;
		candidate = `${base} (${counter})${extension}`;
	}

	taken.add(candidate);
	return candidate;
};

const throwIfAborted = (signal?: AbortSignal) => {
	if (signal?.aborted) {
		throw new DownloadAbortedError();
	}
};

const collectEntries = (data: BallotResultsExport): ZipEntry[] => {
	const entries: ZipEntry[] = [];

	const ballotFileNames = new Set<string>();
	data.ballot.attachments.forEach(({ value }, index) => {
		const fileName = makeUniqueName(
			sanitizeFileName(value.title, `attachment-${index + 1}`),
			ballotFileNames
		);
		entries.push({
			name: `ballot-documents/${fileName}`,
			size: value.size,
			kind: AttachmentKind.Ballot,
			attachmentId: value.id,
		});
	});

	const voteFolderNames = new Set<string>();
	data.votes.forEach((vote, voteIndex) => {
		if (!vote.attachments.length) {
			return;
		}

		const position = String(voteIndex + 1).padStart(2, '0');
		const folder = makeUniqueName(
			sanitizeFileName(
				`${position} - ${vote.firstName} ${vote.lastName} (${vote.organizationName})`,
				`${position} - vote`
			),
			voteFolderNames
		);

		const fileNames = new Set<string>();
		vote.attachments.forEach((attachment: ExportAttachment, index) => {
			const fileName = makeUniqueName(
				sanitizeFileName(attachment.title, `attachment-${index + 1}`),
				fileNames
			);
			entries.push({
				name: `votes/${folder}/${fileName}`,
				size: attachment.size,
				kind: AttachmentKind.Vote,
				attachmentId: attachment.id,
			});
		});
	});

	return entries;
};

const fetchAttachment = async (entry: ZipEntry, signal?: AbortSignal): Promise<Blob> => {
	let lastError: unknown;

	for (let attempt = 1; attempt <= fetchAttempts; attempt += 1) {
		throwIfAborted(signal);
		try {
			return await fetchAttachmentBlob(entry.kind, entry.attachmentId, signal);
		} catch (error) {
			if (signal?.aborted) {
				throw new DownloadAbortedError();
			}
			lastError = error;
		}
	}

	throw lastError;
};

export const buildResultsFileName = (ballotId: number, exportedAt: Date, extension: string) => {
	const date = [
		exportedAt.getFullYear(),
		String(exportedAt.getMonth() + 1).padStart(2, '0'),
		String(exportedAt.getDate()).padStart(2, '0'),
	].join('-');

	return `ballot-${ballotId}-results-${date}.${extension}`;
};

const buildMissingFilesNote = (missing: ZipEntry[]) =>
	[
		'The following files are listed in the ballot but could not be downloaded,',
		'so they are not part of this archive. They are also marked as unavailable',
		'in results.pdf.',
		'',
		...missing.map(({ name }) => name),
		'',
	].join('\n');

/**
 * Renders the results PDF together with every attachment that could be fetched.
 * Attachments that fail are skipped rather than aborting the whole download, so
 * results are always obtainable; they are marked in the PDF and listed in
 * missing-files.txt. With nothing to archive the PDF is served on its own.
 *
 * Attachments are therefore fetched before the PDF is rendered — the PDF has to
 * know which of them are unavailable — which means they are held in memory
 * rather than streamed straight into the archive.
 */
export const downloadBallotResults = async ({
	ballotId,
	data,
	onStart,
	onProgress,
	signal,
}: {
	ballotId: number;
	data: BallotResultsExport;
	/** Fires once the predicted file name and download size are known. */
	onStart?(info: { fileName: string; totalBytes: number }): void;
	onProgress?(progress: DownloadProgress): void;
	signal?: AbortSignal;
}): Promise<{ fileName: string; missing: string[] }> => {
	throwIfAborted(signal);

	// Both libraries are heavy and only ever needed behind this button, so they
	// stay out of the main bundle.
	const [{ pdf }, { BallotResultsPdf }, { downloadZip }] = await Promise.all([
		import('@react-pdf/renderer'),
		import('./ballot-results-pdf'),
		import('client-zip'),
	]);

	throwIfAborted(signal);

	const exportedAt = new Date();
	const entries = collectEntries(data);
	const totalBytes = entries.reduce((total, { size }) => total + size, 0);

	onStart?.({
		fileName: buildResultsFileName(ballotId, exportedAt, entries.length ? 'zip' : 'pdf'),
		totalBytes,
	});

	const fetched: { entry: ZipEntry; blob: Blob }[] = [];
	const missing: ZipEntry[] = [];
	let loadedBytes = 0;

	if (entries.length) {
		onProgress?.({ loadedBytes, totalBytes, percent: 0 });
	}

	for (const entry of entries) {
		try {
			fetched.push({ entry, blob: await fetchAttachment(entry, signal) });
		} catch (error) {
			if (error instanceof DownloadAbortedError) {
				throw error;
			}
			// A single unreachable file must not cost the user the whole export.
			console.error(`Skipping attachment ${entry.name}`, error);
			missing.push(entry);
		}

		loadedBytes += entry.size;
		onProgress?.({
			loadedBytes,
			totalBytes,
			percent: totalBytes > 0 ? Math.min(100, Math.round((loadedBytes / totalBytes) * 100)) : 100,
		});
	}

	throwIfAborted(signal);

	const pdfBlob = await pdf(
		<BallotResultsPdf
			ballotId={ballotId}
			data={data}
			exportedAt={exportedAt}
			missingAttachmentIds={missing.map(({ attachmentId }) => attachmentId)}
		/>
	).toBlob();

	throwIfAborted(signal);

	const missingNames = missing.map(({ name }) => name);

	// Nothing came back, so an archive would hold the PDF and nothing else.
	if (!fetched.length) {
		const pdfName = buildResultsFileName(ballotId, exportedAt, 'pdf');
		downloadBlob(pdfBlob, pdfName);

		return { fileName: pdfName, missing: missingNames };
	}

	const zipEntries = [
		{ name: 'results.pdf', input: pdfBlob, lastModified: exportedAt },
		...fetched.map(({ entry, blob }) => ({
			name: entry.name,
			input: blob,
			lastModified: exportedAt,
		})),
		...(missing.length
			? [
					{
						name: 'missing-files.txt',
						input: buildMissingFilesNote(missing),
						lastModified: exportedAt,
					},
			  ]
			: []),
	];

	const zipName = buildResultsFileName(ballotId, exportedAt, 'zip');
	downloadBlob(await downloadZip(zipEntries).blob(), zipName);

	return { fileName: zipName, missing: missingNames };
};
